import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  OutlinedInput,
  ToggleButton,
  SelectChangeEvent,
} from '@mui/material';
import {
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import type { ProjectFilters as ProjectFiltersType, ProjectStatus } from '../../types/project';
import { PROJECT_STATUS_OPTIONS } from '../../types/project';

interface ProjectFiltersProps {
  filters: ProjectFiltersType;
  onFiltersChange: (filters: ProjectFiltersType) => void;
  clients?: Array<{ _id: string; firstName: string; lastName: string }>;
}

export const ProjectFilters: React.FC<ProjectFiltersProps> = ({
  filters,
  onFiltersChange,
  clients = [],
}) => {
  const handleStatusChange = (event: SelectChangeEvent<ProjectStatus[]>) => {
    const value = event.target.value as ProjectStatus[];
    onFiltersChange({ ...filters, status: value, page: 1 });
  };

  const handleClientChange = (event: SelectChangeEvent<string>) => {
    onFiltersChange({
      ...filters,
      clientId: event.target.value || undefined,
      page: 1,
    });
  };

  const handleSortChange = (event: SelectChangeEvent<string>) => {
    onFiltersChange({
      ...filters,
      sortBy: event.target.value as ProjectFiltersType['sortBy'],
      page: 1,
    });
  };

  const handleArchivedToggle = () => {
    onFiltersChange({
      ...filters,
      includeArchived: !filters.includeArchived,
      page: 1,
    });
  };

  const handleClearFilters = () => {
    onFiltersChange({
      page: 1,
      limit: filters.limit,
    });
  };

  const hasActiveFilters =
    filters.status?.length ||
    filters.clientId ||
    filters.includeArchived;

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        {/* Status Filter */}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            multiple
            value={filters.status || []}
            onChange={handleStatusChange}
            input={<OutlinedInput label="Status" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                  const option = PROJECT_STATUS_OPTIONS.find((o) => o.value === value);
                  return (
                    <Chip
                      key={value}
                      label={option?.label || value}
                      size="small"
                      sx={{
                        backgroundColor: option?.bgColor,
                        color: option?.color,
                      }}
                    />
                  );
                })}
              </Box>
            )}
          >
            {PROJECT_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Chip
                  label={option.label}
                  size="small"
                  sx={{
                    backgroundColor: option.bgColor,
                    color: option.color,
                  }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Client Filter */}
        {clients.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Client</InputLabel>
            <Select
              value={filters.clientId || ''}
              onChange={handleClientChange}
              label="Client"
            >
              <MenuItem value="">
                <em>All Clients</em>
              </MenuItem>
              {clients.map((client) => (
                <MenuItem key={client._id} value={client._id}>
                  {client.firstName} {client.lastName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Sort */}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={filters.sortBy || 'createdAt'}
            onChange={handleSortChange}
            label="Sort By"
          >
            <MenuItem value="createdAt">Created</MenuItem>
            <MenuItem value="updatedAt">Updated</MenuItem>
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="dueDate">Due Date</MenuItem>
            <MenuItem value="status">Status</MenuItem>
          </Select>
        </FormControl>

        {/* Include Archived Toggle */}
        <ToggleButton
          value="archived"
          selected={!!filters.includeArchived}
          onChange={handleArchivedToggle}
          size="small"
          sx={{ px: 2 }}
        >
          <ArchiveIcon fontSize="small" sx={{ mr: 0.5 }} />
          Show Archived
        </ToggleButton>
      </Stack>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <Chip
          label="Clear all filters"
          onDelete={handleClearFilters}
          size="small"
          color="primary"
          variant="outlined"
        />
      )}
    </Box>
  );
};

export default ProjectFilters;
